import { TFunction } from "i18next";

import {
  BuildingConditionValueKeys,
  EnergyClassKeys,
  IndexChartDataResponse,
  IndexChartValueResponse,
  PostGenerateIndexChartDataRequest,
  ValuationRentKeys,
} from "@/api/sprengConnectApi";
import { ChartSeries, ExpenseType, TimeResolution } from "@/types";
import { IndexType } from "@/types/IndexType";

import { formatFloatIndexTrend } from "./formatStringHelpers";

const calcReferenceYearValue = (referenceYearValue: number, currentYearValue: number): number => {
  if (referenceYearValue === 0) return 0;
  return ((currentYearValue - referenceYearValue) / referenceYearValue) * 100;
};

export const calcReferenceYearValues = (referenceYear: number, chartSeries: ChartSeries): (string | number)[] => {
  const seriesHasMonthValues = chartSeries.values.some((value) => value.month != null);
  const referenceYearValue = chartSeries.values.find(
    (value) => value.year === referenceYear && (!seriesHasMonthValues || value.month === 1),
  );
  if (!referenceYearValue) return chartSeries.values.map(() => "-");

  return chartSeries.values.map((value) =>
    referenceYearValue.y == null || value.y == null || referenceYearValue.y === 0
      ? "-"
      : formatFloatIndexTrend(calcReferenceYearValue(referenceYearValue.y, value.y)),
  );
};

export const getSeriesName = (
  t: TFunction<"PlaygroundPage", undefined>,
  request: PostGenerateIndexChartDataRequest,
  indexChartData?: IndexChartDataResponse,
  energyEfficiencyGroup?: EnergyClassKeys,
  valuationOrRent?: ValuationRentKeys,
  objectStateSection?: BuildingConditionValueKeys,
): string => {
  let seriesName = `${request.category} - `;
  if (indexChartData == null) {
    seriesName += t("chart.seriesLoadingError");
    return seriesName;
  }
  const expenseType: ValuationRentKeys | undefined = request.expenseType
    ? request.expenseType === ExpenseType.Rent
      ? "rent"
      : "valuation"
    : valuationOrRent;

  const firstRegion = indexChartData.values.find((value) => value.region != null)?.region;

  seriesName += request.address.zip
    ? `${request.address.zip}${firstRegion != null ? ` ${firstRegion}` : ""}`
    : request.area_type
      ? `${t("chart.comparison.germany")} - ${t("filter.compareTypeSection.areaType", { context: request.area_type })}`
      : t("chart.comparison.germany");

  if (energyEfficiencyGroup) {
    seriesName += ` - ${t("filter.energyEfficiencySection.options", { context: energyEfficiencyGroup as string })}`;
  }

  if (objectStateSection) {
    seriesName += ` - ${t("filter.objectStateSection.options", { context: objectStateSection as string })}`;
  }

  if (expenseType) {
    seriesName += ` - ${t("filter.expenseTypeSection.options", { context: expenseType as string })}`;
  }

  return seriesName;
};

export const getIndexValue = (
  index: IndexType | undefined,
  value: IndexChartValueResponse,
  energyClass: EnergyClassKeys | undefined,
  valuationOrRent?: ValuationRentKeys,
  buildingConditionValue?: BuildingConditionValueKeys,
): number | undefined => {
  if (index === undefined) {
    return undefined;
  }
  switch (index) {
    case IndexType.SEPI:
      if (value.energy_class && energyClass) {
        return value.energy_class[energyClass];
      } else return undefined;
    case IndexType.SIPN:
      if (valuationOrRent && buildingConditionValue && value[valuationOrRent]) {
        return value[valuationOrRent][buildingConditionValue];
      }
      break;
    case IndexType.SIM:
    case IndexType.SIPI:
    default:
      return value.index;
  }
};

export const getDateFormat = (selectedIndex: IndexType, timeResolution?: TimeResolution): string => {
  switch (selectedIndex) {
    case IndexType.SIM:
      return timeResolution === TimeResolution.Quarterly ? "%{x|Q%q/%Y}" : "%{x|%Y}";
    case IndexType.SIPI:
      return timeResolution === TimeResolution.Monthly ? "%{x|%m/%Y}" : "%{x|Q%q/%Y}";
    case IndexType.SIPN:
      return "%{x|Q%q/%Y}";
    case IndexType.SEPI:
      return "%{x|%m/%Y}";
    default:
      return "%{x|%Y}";
  }
};
